import React, { useEffect } from 'react';
import {
  fileInputMultipleUI,
  fileInputMultipleSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { trackShaPageSeen } from '../../utils/tracking/bddShaTracking';
import {
  createUploadPayload,
  trackShaUploadValidation,
} from '../../utils/uploadHelpers';
import {
  MAX_FILE_SIZE_BYTES,
  MAX_PDF_FILE_SIZE_BYTES,
  SHA_PART_A_PDF_URL,
} from '../../../shared/constants';
import { UPLOAD_URL } from '../../../shared/components/fileInputComponent/constants';
import UploadValidationWarning from '../../components/UploadValidationWarning';

const SHA_ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.gif,.bmp';
const SHA_ATTACHMENT_ID = 'L1839';
const SHA_FILE_INPUT_LABEL =
  'Upload your Separation Health Assessment (self-assessment, also called “Part A”) to support your claim.';
const SHA_HINT_TEXT =
  'You can upload a .pdf, .jpg, .jpeg, .png, .gif, or .bmp file. Each file should be no larger than 100 MB. Larger files may take longer to upload, depending on the internet connection.';
const SHA_WARNING_TITLE = <h3>Make sure you upload the correct file</h3>;

// Appends attachment_id (needed for backend document validation) only when the
// document validation toggle is on.
const createPayload = createUploadPayload({ attachmentId: SHA_ATTACHMENT_ID });

// `va-file-input-multiple` needs the original File in the parsed result. Warnings
// from the backend are always carried through; the alert's own toggle gate
// (UploadValidationWarning) controls whether they're surfaced. attachmentId is
// intentionally omitted; the submit transform assigns it from the warnings.
// trackShaUploadValidation emits the per-upload validation RUM event (no-op when
// the doc-validation toggle is off).
const parseShaResponse = (response, file, options) => {
  trackShaUploadValidation(response, file, options, 'v2');
  return {
    name: file?.name,
    confirmationCode: response?.data?.attributes?.guid,
    warnings: response?.data?.attributes?.warnings || [],
    file,
  };
};

/**
 * Tracks that the SHA upload page has been seen.
 * Rendered as part of ui:description so it fires on page mount.
 */
const ShaUploadDescription = () => {
  useEffect(() => {
    trackShaPageSeen('upload');
  }, []);

  return (
    <div className="vads-u-margin-y--2">
      <UploadValidationWarning
        formDataPath="separationHealthAssessmentUploads"
        warningTitle={SHA_WARNING_TITLE}
        scrollOffset={10}
      >
        <p>
          You may have uploaded a file that’s not the Separation Health
          Assessment (self-assessment, also called “Part A”).{' '}
        </p>
        <p>
          <va-link
            external
            href={SHA_PART_A_PDF_URL}
            text="Download the Separation Health Assessment (PDF)"
          />
        </p>
        <p>
          <strong>Note: </strong>
          You can continue completing this claim without uploading a new file.
          But you may not qualify for the Benefits Delivery at Discharge (BDD)
          Program.
        </p>
      </UploadValidationWarning>
    </div>
  );
};

export const uiSchema = {
  'ui:title': (
    <h3 className="vads-u-margin-y--0 vads-u-color--base">
      Upload your Separation Health Assessment
    </h3>
  ),
  'ui:description': <ShaUploadDescription />,
  separationHealthAssessmentUploads: {
    ...fileInputMultipleUI({
      title: SHA_FILE_INPUT_LABEL,
      required: true,
      skipUpload: false,
      fileUploadUrl: UPLOAD_URL,
      formNumber: '21-526EZ',
      fileSizesByFileType: {
        pdf: {
          maxFileSize: MAX_PDF_FILE_SIZE_BYTES,
          minFileSize: 1024,
        },
        default: {
          maxFileSize: MAX_FILE_SIZE_BYTES,
          minFileSize: 1,
        },
      },
      accept: SHA_ACCEPTED_FILE_TYPES,
      hint: SHA_HINT_TEXT,
      createPayload,
      parseResponse: parseShaResponse,
    }),
    'ui:confirmationField': ({ formData }) => ({
      data: formData?.map(item => item.name || item.fileName),
      label: 'Uploaded file(s)',
    }),
  },
};

export const schema = {
  type: 'object',
  required: ['separationHealthAssessmentUploads'],
  properties: {
    separationHealthAssessmentUploads: {
      ...fileInputMultipleSchema(),
      minItems: 1,
      maxItems: 20,
    },
  },
};
